/**
 * Moving a player between devices: a QR code that opens the game with their
 * progress, and a save picture (a PNG of that QR code with their name and
 * stars) that can be loaded back in Settings.
 *
 * The link carries the code after a "#", which browsers never send to the
 * server, so a child's progress goes from screen to screen and nowhere else.
 */
import qrcode from 'qrcode-generator';

export const SAVE_HASH = '#save=';

/** "1 level", "2 levels". */
export function countOf(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** The link a QR code holds: this site, with the player's code after #save=. */
export function saveLink(code: string): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}${SAVE_HASH}${code}`;
}

/**
 * The QR code as rows of dark (true) and light modules.
 *
 * Error correction L leaves the most room for data. The code is shown on a
 * clean screen or a crisp picture rather than printed on a scuffed sticker,
 * so the extra robustness of M or above isn't needed.
 */
export function qrModules(text: string): boolean[][] {
  const qr = qrcode(0, 'L');
  qr.addData(text, 'Byte');
  qr.make();
  const count = qr.getModuleCount();
  return Array.from({ length: count }, (_, row) =>
    Array.from({ length: count }, (_, col) => qr.isDark(row, col)),
  );
}

export interface SaveCardInfo {
  readonly name: string;
  readonly stars: number;
  readonly levels: number;
  readonly link: string;
}

const NAVY = '#1b2a41';
const BODY = '#2f3a4a';
const FACE = '#f8faff';
const BEAK = '#f59a5b';
const BLUSH = 'rgba(255, 143, 163, 0.55)';

/**
 * The QR code in the modern style: rounded corner markers with gold centres,
 * and the data as soft dots that join into rounded bars where they touch.
 *
 * Still a fully standard QR code - every dark module is dark and every light
 * one light - so phone cameras read it as easily as the square-pixel kind.
 * Drawn into the square (x, y, size), which includes a light margin.
 */
export function drawStyledQr(
  ctx: CanvasRenderingContext2D,
  modules: boolean[][],
  x: number,
  y: number,
  size: number,
): void {
  const n = modules.length;
  // The standard four-module clear border, inside the white card.
  const quiet = 4;
  // Whole pixels per module, centred in the space: fractional sizes leave
  // faint anti-aliased seams between modules that readers can trip on.
  const cell = Math.max(1, Math.floor(size / (n + quiet * 2)));
  const used = cell * (n + quiet * 2);
  const ox = Math.round(x + (size - used) / 2) + quiet * cell;
  const oy = Math.round(y + (size - used) / 2) + quiet * cell;
  const dark = (r: number, c: number): boolean => modules[r]?.[c] === true;
  const inFinder = (r: number, c: number): boolean =>
    (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);

  // Data: a dot per dark module, bridged to dark neighbours right and below.
  ctx.fillStyle = NAVY;
  const radius = cell * 0.46;
  const bridge = cell * 0.92;
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (!dark(r, c) || inFinder(r, c)) continue;
      const cx = ox + (c + 0.5) * cell;
      const cy = oy + (r + 0.5) * cell;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
      if (c + 1 < n && dark(r, c + 1) && !inFinder(r, c + 1)) {
        // One pixel of overlap each end, so joins have no hairline seams.
        ctx.fillRect(cx - 1, cy - bridge / 2, cell + 2, bridge);
      }
      if (r + 1 < n && dark(r + 1, c) && !inFinder(r + 1, c)) {
        ctx.fillRect(cx - bridge / 2, cy - 1, bridge, cell + 2);
      }
    }
  }

  // Corner markers: rounded ring and rounded centre. Both stay dark: a reader
  // turns the picture black and white first, and a light centre (gold, say)
  // comes out white and the marker is no longer found.
  for (const [r, c] of [
    [0, 0],
    [0, n - 7],
    [n - 7, 0],
  ] as const) {
    const fx = ox + c * cell;
    const fy = oy + r * cell;
    ctx.fillStyle = NAVY;
    roundRect(ctx, fx, fy, cell * 7, cell * 7, cell * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, fx + cell, fy + cell, cell * 5, cell * 5, cell * 1.4);
    ctx.fill();
    ctx.fillStyle = NAVY;
    roundRect(ctx, fx + cell * 2, fy + cell * 2, cell * 3, cell * 3, cell * 1.1);
    ctx.fill();
  }
}

/**
 * A penguin whose white belly is the QR code: head with a tuft, eyes, blush
 * and beak above, flippers either side, feet below. Nothing is drawn over
 * the code itself, so it scans exactly as a plain one would.
 *
 * `qrSize` is the belly's square; the penguin is drawn centred on cx with its
 * head top at `top`. Returns the height it used.
 */
export function drawPenguinQr(
  ctx: CanvasRenderingContext2D,
  modules: boolean[][],
  cx: number,
  top: number,
  qrSize: number,
): number {
  const q = qrSize;
  const headR = q * 0.21;
  const headCy = top + headR * 1.25;
  const bodyTop = headCy + headR * 0.55;
  const bodyW = q * 1.14;
  const bodyH = q * 1.16;
  const bellyTop = bodyTop + q * 0.07;

  // Flippers, behind the body.
  ctx.fillStyle = BODY;
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(cx + side * (bodyW / 2 - q * 0.02), bodyTop + q * 0.3);
    ctx.rotate(side * -0.35);
    ctx.beginPath();
    ctx.ellipse(side * q * 0.05, q * 0.15, q * 0.07, q * 0.22, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  // Feet.
  ctx.fillStyle = BEAK;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(cx + side * q * 0.2, bodyTop + bodyH, q * 0.13, q * 0.045, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  // Body, with the white belly holding the code. The belly's corners are kept
  // tighter than the code's clear border, so they never clip a corner marker.
  ctx.fillStyle = BODY;
  roundRect(ctx, cx - bodyW / 2, bodyTop, bodyW, bodyH, q * 0.2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, cx - q / 2, bellyTop, q, q, q * 0.04);
  ctx.fill();
  drawStyledQr(ctx, modules, cx - q / 2, bellyTop, q);

  drawPenguinHead(ctx, cx, headCy, headR / 34);

  return bodyTop + bodyH + q * 0.05 - top;
}

/**
 * The penguin's head: tuft, dark head, white heart-shaped face, eyes, blush
 * and beak, centred on (cx, headCy). One unit `s` is a 34th of its radius.
 */
function drawPenguinHead(
  ctx: CanvasRenderingContext2D,
  cx: number,
  headCy: number,
  s: number,
): void {
  const headR = 34 * s;
  ctx.fillStyle = BODY;
  ctx.beginPath();
  ctx.moveTo(cx - 4 * s, headCy - headR + 2 * s);
  ctx.quadraticCurveTo(cx - 2 * s, headCy - headR - 12 * s, cx + 7 * s, headCy - headR - 13 * s);
  ctx.quadraticCurveTo(cx + 3 * s, headCy - headR - 5 * s, cx + 8 * s, headCy - headR - 2 * s);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx, headCy, headR * 1.12, headR, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = FACE;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(cx + side * 12 * s, headCy + 1 * s, 16 * s, 20 * s, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.ellipse(cx, headCy + 12 * s, 27 * s, 16 * s, 0, 0, Math.PI * 2);
  ctx.fill();
  for (const side of [-1, 1]) {
    const ex = cx + side * 12 * s;
    ctx.fillStyle = NAVY;
    ctx.beginPath();
    ctx.ellipse(ex, headCy, 5.2 * s, 6.2 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(ex + 1.8 * s, headCy - 2.2 * s, 2 * s, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = BLUSH;
    ctx.beginPath();
    ctx.ellipse(cx + side * 21 * s, headCy + 9 * s, 5 * s, 2.8 * s, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = BEAK;
  ctx.beginPath();
  ctx.moveTo(cx - 6 * s, headCy + 8 * s);
  ctx.lineTo(cx + 6 * s, headCy + 8 * s);
  ctx.lineTo(cx, headCy + 14 * s);
  ctx.closePath();
  ctx.fill();
}

/**
 * The panel's QR: the styled code at full size on a white card, with the
 * penguin's head peeking over the top edge. The card's border is wide enough
 * that the head never covers the code.
 */
export function peekingQrCanvas(link: string, qrSize: number): HTMLCanvasElement {
  const headR = qrSize * 0.075;
  const cardTop = Math.round(headR * 1.5);
  const pad = Math.round(qrSize * 0.03);
  const canvas = document.createElement('canvas');
  canvas.width = qrSize;
  canvas.height = cardTop + qrSize;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return canvas;
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, 0, cardTop, qrSize, qrSize, qrSize * 0.04);
  ctx.fill();
  drawStyledQr(ctx, qrModules(link), pad, cardTop + pad, qrSize - pad * 2);
  drawPenguinHead(ctx, qrSize / 2, cardTop - headR * 0.6, headR / 34);
  return canvas;
}

/** A save picture: the player's name, their stars and the penguin QR, as a PNG. */
export async function renderSaveCard(info: SaveCardInfo): Promise<Blob> {
  const width = 1080;
  const height = 1700;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('no 2d canvas');

  const sky = ctx.createLinearGradient(0, 0, 0, height);
  sky.addColorStop(0, '#5fa8d8');
  sky.addColorStop(0.55, '#cfe8f7');
  sky.addColorStop(1, '#f4f9ff');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, width, height);

  const font = (weight: number, size: number): string =>
    `${weight} ${size}px "Atkinson Hyperlegible", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff';
  ctx.font = font(700, 92);
  ctx.fillText('Quest Jump', width / 2, 118);

  ctx.fillStyle = NAVY;
  ctx.font = font(700, 64);
  ctx.fillText(info.name, width / 2, 214);
  ctx.fillStyle = '#b36b00';
  ctx.font = font(700, 40);
  ctx.fillText(
    `${countOf(info.stars, 'star')}   ·   ${countOf(info.levels, 'level')}`,
    width / 2,
    272,
  );

  const used = drawPenguinQr(ctx, qrModules(info.link), width / 2, 312, 760);

  ctx.fillStyle = NAVY;
  ctx.font = font(400, 34);
  const textTop = 312 + used + 44;
  ctx.fillText('Scan with a phone or tablet camera to keep playing there,', width / 2, textTop);
  ctx.fillText('or load this picture in Settings.', width / 2, textTop + 46);
  ctx.fillStyle = '#4a5e80';
  ctx.font = font(400, 28);
  ctx.fillText(`Saved ${new Date().toLocaleDateString()}`, width / 2, textTop + 96);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob === null ? reject(new Error('no PNG')) : resolve(blob)),
      'image/png',
    );
  });
}

/** Save a file to the player's downloads. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Let the player pick a picture, and read the QR code in it. Null if none. */
export async function readQrFromPicture(): Promise<string | null> {
  const file = await pickImageFile();
  if (file === null) return null;
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;
  ctx.drawImage(bitmap, 0, 0);
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  // Loaded only when needed: the reader is big, and most visits never use it.
  const { default: jsQR } = await import('jsqr');
  return jsQR(image.data, image.width, image.height)?.data ?? null;
}

function pickImageFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', () => resolve(input.files?.[0] ?? null), { once: true });
    input.addEventListener('cancel', () => resolve(null), { once: true });
    input.click();
  });
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
